import { useState, useRef, useEffect } from 'react'
import { useLanguage } from '@/contexts/LanguageContext'
import { turbineService, type Turbine } from '@/services/turbineService'
import { companyService, type Company } from '@/services/companyService'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'
import useAppStore from '@/store/appStore'

export const useTurbineManagementViewModel = () => {
  const { t } = useLanguage()
  const [selectedCity, setSelectedCity] = useState<string>('all')
  const [selectedCompany, setSelectedCompany] = useState<string>('all')
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [deletingTurbine, setDeletingTurbine] = useState<Turbine | null>(null)
  const [editingTurbine, setEditingTurbine] = useState<Turbine | null>(null)
  const [turbines, setTurbines] = useState<Turbine[]>([])
  const [loading, setLoading] = useState(true)

  // Hata state'leri modal basina ayrildi.
  // Tek bir `error` state'i hem sayfa banner'inda hem modal icinde gosteriliyordu:
  // ayni hata iki yerde birden cikiyor, modal disi bir hata modal acilinca
  // modalin icinde beliriyordu. Artik `loadError` YALNIZ modal disi (liste/sirket
  // yukleme) hatalarini tasir; her modal kendi hatasini gosterir.
  const [loadError, setLoadError] = useState<string | null>(null)
  /**
   * `loadError` UC ayri kaynaktan yazilabiliyor (oturum, sirketler, turbinler).
   * Kaynagi bilmezsek "Tekrar dene" yanlis yuklemeyi tetikler: sirket yuklemesi
   * basarisizken tekrar denemek `loadTurbines`i cagirir, o da `setLoadError(null)`
   * yapip hatayi SILER -- sorun surerken banner kaybolur.
   */
  const [loadErrorSource, setLoadErrorSource] = useState<'user' | 'companies' | 'turbines' | null>(null)

  const reportLoadError = (source: 'user' | 'companies' | 'turbines', message: string): void => {
    setLoadError(message)
    setLoadErrorSource(source)
  }
  const [addError, setAddError] = useState<string | null>(null)
  const [editError, setEditError] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Cift gonderim korumasi (her submit tek ucusta).
  const [isAddingTurbine, setIsAddingTurbine] = useState(false)
  const [isSavingTurbine, setIsSavingTurbine] = useState(false)
  const [isDeletingTurbine, setIsDeletingTurbine] = useState(false)

  const [currentUser, setCurrentUser] = useState<any>(null)
  const [companies, setCompanies] = useState<Company[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | 'all'>('all')
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false)
  const [isAddTurbineCompanyDropdownOpen, setIsAddTurbineCompanyDropdownOpen] = useState(false)
  const [selectedAddTurbineCompanyId, setSelectedAddTurbineCompanyId] = useState<number | 'all'>('all')
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false)

  const [newTurbine, setNewTurbine] = useState({
    turbine_name: '',
    city: '',
    code: '',
    location: '',
    site_name: '',
    coordinates: '',
    rooms: '3' // Default 3 rooms
  })
  const [editTurbine, setEditTurbine] = useState({
    turbine_name: '',
    city: '',
    code: '',
    location: '',
    site_name: '',
    coordinates: '',
    company_id: 0
  })
  const [validationErrors, setValidationErrors] = useState({
    rooms: ''
  })
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const companyDropdownRef = useRef<HTMLDivElement>(null)
  const addTurbineCompanyDropdownRef = useRef<HTMLDivElement>(null)
  const yearDropdownRef = useRef<HTMLDivElement>(null)

  // Check if current user is admin or superadmin
  const isAdmin = currentUser?.role === 'admin'
  const isSuperAdmin = currentUser?.role === 'superadmin'
  const hasAdminAccess = isAdmin || isSuperAdmin
  const hasSuperAdminAccess = isSuperAdmin

  // Listen for turbine refresh trigger
  const turbineRefreshTrigger = useAppStore(state => state.turbineRefreshTrigger)



  // Load current user from localStorage
  useEffect(() => {
    const loadCurrentUser = () => {
      try {
        const userData = localStorage.getItem('deepwind-user')
        if (userData) {
          const user = JSON.parse(userData)
          setCurrentUser(user)
        } else {
          reportLoadError('user', t.errors.sessionExpired)
        }
      } catch (error) {
        logError('TurbineManagement.loadCurrentUser', error)
        reportLoadError('user', t.errors.sessionExpired)
      }
    }
    
    loadCurrentUser()
  }, [])

  // Get unique cities for filtering
  const cities = ['all', ...Array.from(new Set(turbines.map(t => t.city).filter(Boolean)))]
  const cityOptions = [
    { value: 'all', label: t.dashboard.allCities },
    ...cities.slice(1).sort().map(city => ({ value: city, label: city }))
  ]
  
  // Get unique years from turbines (based on updated_at or created_at)
  const availableYears = Array.from(new Set(
    turbines.map(t => {
      const date = t.updated_at ? new Date(t.updated_at) : (t.created_at ? new Date(t.created_at) : new Date());
      return date.getFullYear();
    }).filter(Boolean)
  )).sort((a, b) => b - a) // Sort descending (newest first)
  
  const yearOptions = [
    { value: 'all', label: t.dashboard.allYears },
    ...availableYears.map(year => ({ value: year, label: year.toString() }))
  ]

  // Load companies from API (for admin users)
  const loadCompanies = async () => {
    try {
      const response = await companyService.getCompanies()
      
      if (response.success && Array.isArray(response.data)) {
        setCompanies(response.data)
      } else {
        reportLoadError('companies', responseErrorMessage(response, t, t.dashboard.errorLoadingCompanies))
      }
    } catch (error) {
      logError('TurbineManagement.loadCompanies', error)
      reportLoadError('companies', toUserMessage(error, t, t.dashboard.errorLoadingCompanies))
    }
  }


  // Load turbines from API with role-based filtering
  const loadTurbines = async () => {
    if (!currentUser) {
      return
    }

    try {
      setLoading(true)
      // Yalniz kendi hatasini temizle: sirket/oturum kaynakli bir hata bu yuklemeyle
      // cozulmedigi icin ekranda kalmali.
      setLoadError((current) => (loadErrorSource === 'turbines' ? null : current))
      
      // Clear cache for stale data
      
      const response = await turbineService.getTurbines(
        currentUser.id,
        currentUser.role,
        isSuperAdmin ? undefined : currentUser.company_id, // SuperAdmin sees all companies
        isSuperAdmin && selectedCompanyId === 'all' ? undefined : selectedCompanyId
      )
      
      if (response.success && Array.isArray(response.data)) {
        setTurbines(response.data)
      } else {
        // Hata bos veri DEGILDIR: eldeki liste korunur, ustte "yuklenemedi + tekrar dene" cikar.
        reportLoadError('turbines', responseErrorMessage(response, t, t.dashboard.errorLoadingTurbines))
      }
    } catch (error) {
      logError('TurbineManagement.loadTurbines', error)
      reportLoadError('turbines', toUserMessage(error, t, t.dashboard.errorLoadingTurbines))
    } finally {
      setLoading(false)
    }
  }

  // Load turbines when current user is available
  useEffect(() => {
    if (currentUser) {
      loadTurbines()
      
      // Load companies if user is superadmin
      if (currentUser.role === 'superadmin') {
        loadCompanies()
      }
    }
  }, [currentUser])

  // Reload turbines when superadmin changes company filter
  useEffect(() => {
    if (currentUser && currentUser.role === 'superadmin') {
      loadTurbines()
    }
  }, [selectedCompanyId])

  // Reload turbines when refresh trigger changes (e.g., from TurbineDetailScreen)
  useEffect(() => {
    if (currentUser && turbineRefreshTrigger > 0) {
      loadTurbines()
    }
  }, [turbineRefreshTrigger])




  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target as Node)) {
        setIsCompanyDropdownOpen(false)
      }
      if (addTurbineCompanyDropdownRef.current && !addTurbineCompanyDropdownRef.current.contains(event.target as Node)) {
        setIsAddTurbineCompanyDropdownOpen(false)
      }
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target as Node)) {
        setIsYearDropdownOpen(false)
      }

    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close all dropdowns when modal opens
  useEffect(() => {
    if (showAddModal || showDeleteModal) {
      setIsDropdownOpen(false)
      setIsCompanyDropdownOpen(false)
      setIsAddTurbineCompanyDropdownOpen(false)
    }
  }, [showAddModal, showDeleteModal])

  // NOT: ESC ile kapatma artik ortak `Modal` bileseninde. Modal basina kapanis
  // temizligi asagidaki tek closeXModal fonksiyonlarinda toplandi (once ayni
  // reset blogu backdrop/ESC/X/Iptal icin dort kez kopyalanmisti).

  const closeAddModal = () => {
    setShowAddModal(false)
    setNewTurbine({ turbine_name: '', city: '', code: '', rooms: '3', location: '', site_name: '', coordinates: '' })
    setValidationErrors({ rooms: '' })
    setSelectedAddTurbineCompanyId('all')
    setIsDropdownOpen(false)
    setIsCompanyDropdownOpen(false)
    setIsAddTurbineCompanyDropdownOpen(false)
    setAddError(null)
  }

  const closeEditModal = () => {
    setShowEditModal(false)
    setEditingTurbine(null)
    setEditTurbine({ turbine_name: '', city: '', code: '', location: '', site_name: '', coordinates: '', company_id: 0 })
    setEditError(null)
  }

  const closeDeleteModal = () => {
    setShowDeleteModal(false)
    setDeletingTurbine(null)
    setDeleteError(null)
  }

  // Filter and sort turbines alphabetically
  const filteredTurbines = turbines.filter(turbine => {
    const matchesCity = selectedCity === 'all' || turbine.city === selectedCity
    const matchesCompany = selectedCompany === 'all' || (turbine.company_id && turbine.company_id.toString() === selectedCompany)

    // Year filtering based on updated_at or created_at
    const matchesYear = selectedYear === 'all' || (() => {
      const date = turbine.updated_at ? new Date(turbine.updated_at) : (turbine.created_at ? new Date(turbine.created_at) : new Date());
      return date.getFullYear() === selectedYear;
    })()

    const matchesSearch = (turbine.code?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (turbine.city?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (turbine.location?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (turbine.turbine_name?.toLowerCase() || '').includes(searchTerm.toLowerCase())

    return matchesCity && matchesCompany && matchesYear && matchesSearch
  }).sort((a, b) => {
    // Alphabetically sort by turbine_name (or fallback to code/city)
    const nameA = (a.turbine_name || a.name || a.code || a.city || '').toLowerCase()
    const nameB = (b.turbine_name || b.name || b.code || b.city || '').toLowerCase()
    return nameA.localeCompare(nameB, 'tr') // Turkish locale for proper sorting
  })

  const handleAddTurbine = async () => {
    if (isAddingTurbine) return
    setAddError(null)

    // Eksik alanla butona basildiginda eskiden HICBIR SEY olmuyordu (dis if'in
    // else'i yoktu). Artik hangi alanlarin eksik oldugu modal icinde soyleniyor.
    const missingFields: string[] = []
    if (!newTurbine.city.trim()) missingFields.push(t.dashboard.city)
    if (!newTurbine.code.trim()) missingFields.push(t.dashboard.turbineCode)
    if (!newTurbine.location.trim()) missingFields.push(t.dashboard.location)
    if (!newTurbine.rooms.trim()) missingFields.push(t.turbine.roomCount)

    if (missingFields.length > 0) {
      setAddError(`${t.errors.validation} (${missingFields.join(', ')})`)
      return
    }

    // Validate room count
    const roomCount = parseInt(newTurbine.rooms)
    if (isNaN(roomCount) || roomCount <= 0) {
      const roomsMessage = 'Oda sayısı 1 veya daha büyük olmalıdır' // TODO(i18n)
      setValidationErrors(prev => ({ ...prev, rooms: roomsMessage }))
      setAddError(roomsMessage)
      return
    }

    // Determine company_id based on user role
    let companyId: number
    if (currentUser?.role === 'admin' || currentUser?.role === 'superadmin') {
      // Admin/SuperAdmin must select a company from the modal dropdown
      if (selectedAddTurbineCompanyId === 'all') {
        setAddError(t.dashboard.pleaseSelectCompany)
        return
      }
      companyId = selectedAddTurbineCompanyId as number
    } else {
      // Company owner uses their own company
      companyId = currentUser?.company_id
    }

    const turbineData = {
      turbine_name: newTurbine.turbine_name,
      city: newTurbine.city,
      code: newTurbine.code,
      rooms: roomCount,
      location: newTurbine.location,
      site_name: newTurbine.site_name,
      coordinates: newTurbine.coordinates,
      company_id: companyId
    }

    setIsAddingTurbine(true)
    try {
      const response = await turbineService.addTurbine(turbineData)

      if (response.success) {
        // Add new turbine to current list immediately for instant UI update
        if (response.turbine) {
          setTurbines(prevTurbines => [response.turbine!, ...prevTurbines])
        }

        closeAddModal()

        // Reset filters to show the newly added turbine
        setSelectedCity('all')
        setSelectedCompany('all')
        setSelectedYear('all')
        setSearchTerm('')
      } else {
        // Show specific error message from backend (e.g., duplicate code)
        setAddError(responseErrorMessage(response, t, t.dashboard.errorAddingTurbine))
      }
    } catch (error) {
      logError('TurbineManagement.addTurbine', error)
      setAddError(toUserMessage(error, t, t.dashboard.errorAddingTurbine))
    } finally {
      setIsAddingTurbine(false)
    }
  }


  const handleDeleteTurbine = (turbine: Turbine) => {
    setDeleteError(null)
    setDeletingTurbine(turbine)
    setShowDeleteModal(true)
  }

  const handleEditTurbine = (turbine: Turbine) => {
    setEditError(null)
    setEditingTurbine(turbine)
    setEditTurbine({
      turbine_name: turbine.turbine_name || '',
      city: turbine.city || '',
      code: turbine.code || '',
      location: turbine.location || '',
      site_name: turbine.site_name || '',
      coordinates: turbine.coordinates || '',
      company_id: turbine.company_id || 0
    })
    setShowEditModal(true)
  }

  const confirmDeleteTurbine = async () => {
    if (isDeletingTurbine) return
    if (!deletingTurbine) return
    if (!currentUser) {
      setDeleteError(t.errors.sessionExpired)
      return
    }

    setDeleteError(null)
    setIsDeletingTurbine(true)
    try {
      const response = await turbineService.deleteTurbine(
        deletingTurbine.id, 
        currentUser.id, 
        currentUser.role,
        currentUser.company_id || 0
      );
      
      if (response.success) {
        // Remove the deleted turbine from the state
        setTurbines(prev => prev.filter(t => t.id !== deletingTurbine.id));
        closeDeleteModal()
      } else {
        // Yikici islem basarisiz: modal ACIK kalir, hata modalin icinde durur.
        setDeleteError(responseErrorMessage(response, t, t.dashboard.errorDeletingTurbine))
      }
    } catch (error) {
      logError('TurbineManagement.deleteTurbine', error)
      setDeleteError(toUserMessage(error, t, t.dashboard.errorDeletingTurbine))
    } finally {
      setIsDeletingTurbine(false)
    }
  }

  const confirmEditTurbine = async () => {
    if (isSavingTurbine) return
    if (!editingTurbine) return

    setEditError(null)
    setIsSavingTurbine(true)
    try {
      // Create a new object for the update data
      const updateData = {
        turbine_name: editTurbine.turbine_name,
        city: editTurbine.city,
        code: editTurbine.code,
        rooms: editingTurbine.rooms || 3,
        location: editTurbine.location,
        site_name: editTurbine.site_name,
        coordinates: editTurbine.coordinates,
        status: editingTurbine.status || 'active',
        company_id: editTurbine.company_id
      };
      
      const response = await turbineService.updateTurbine(editingTurbine.id, updateData);
      
      if (response.success) {
        // Update the turbine in the list with the returned data
        // (sunucu turbini geri dondurmezse gonderilen veriyle guncellenir)
        const updated = response.turbine ?? updateData
        setTurbines(prev => prev.map(t => 
          t.id === editingTurbine.id 
            ? { ...t, ...updated } 
            : t
        ));
        
        closeEditModal()
      } else {
        // Ornek: ayni ada sahip turbin. Bu mesaj eskiden hicbir yerde gorunmuyordu.
        setEditError(responseErrorMessage(response, t, t.dashboard.errorUpdatingTurbine))
      }
    } catch (error) {
      logError('TurbineManagement.updateTurbine', error)
      setEditError(toUserMessage(error, t, t.dashboard.errorUpdatingTurbine))
    } finally {
      setIsSavingTurbine(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'maintenance': return 'bg-yellow-100 text-yellow-800'
      case 'faulty': return 'bg-red-100 text-red-800'
      case 'offline': return 'bg-gray-100 text-gray-800'
      case 'inactive': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return t.dashboard.active
      case 'maintenance': return t.dashboard.maintenance
      case 'faulty': return t.dashboard.faulty
      case 'offline': return t.dashboard.offline
      case 'inactive': return t.dashboard.inactive
      default: return t.dashboard.unknown
    }
  }

  // Additional permission checks
  const isOperator = currentUser?.role === 'operator'
  const isCustomer = currentUser?.role === 'customer'
  const canAddTurbine = hasAdminAccess // Only admin and superadmin can add turbines

  return {
    // state
    selectedCity,
    setSelectedCity,
    selectedYear,
    setSelectedYear,
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    showAddModal,
    setShowAddModal,
    showDeleteModal,
    setShowDeleteModal,
    showEditModal,
    setShowEditModal,
    deletingTurbine,
    setDeletingTurbine,
    editingTurbine,
    setEditingTurbine,
    loading,
    currentUser,
    companies,
    selectedCompanyId,
    setSelectedCompanyId,
    isCompanyDropdownOpen,
    setIsCompanyDropdownOpen,
    isAddTurbineCompanyDropdownOpen,
    setIsAddTurbineCompanyDropdownOpen,
    selectedAddTurbineCompanyId,
    setSelectedAddTurbineCompanyId,
    isYearDropdownOpen,
    setIsYearDropdownOpen,
    newTurbine,
    setNewTurbine,
    editTurbine,
    setEditTurbine,
    validationErrors,
    setValidationErrors,
    isDropdownOpen,
    setIsDropdownOpen,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    // errors (modal basina ayri)
    loadError,
    dismissLoadError: () => {
      setLoadError(null)
      setLoadErrorSource(null)
    },
    /**
     * Hatayi URETEN yuklemeyi tekrar dener. Oturum hatasinda tekrar denemenin
     * faydasi yok (kullanicinin yeniden giris yapmasi gerekir), o yuzden ekran
     * o durumda "Tekrar dene" gostermesin diye undefined donuyor.
     */
    retryLoad:
      loadErrorSource === 'companies'
        ? loadCompanies
        : loadErrorSource === 'turbines'
          ? loadTurbines
          : undefined,
    addError,
    dismissAddError: () => setAddError(null),
    editError,
    dismissEditError: () => setEditError(null),
    deleteError,
    dismissDeleteError: () => setDeleteError(null),
    // submit durumlari (cift gonderim korumasi)
    isAddingTurbine,
    isSavingTurbine,
    isDeletingTurbine,
    // refs
    dropdownRef,
    companyDropdownRef,
    addTurbineCompanyDropdownRef,
    yearDropdownRef,
    // derived
    isSuperAdmin,
    hasAdminAccess,
    hasSuperAdminAccess,
    canAddTurbine,
    cityOptions,
    yearOptions,
    filteredTurbines,
    // actions
    closeAddModal,
    closeEditModal,
    closeDeleteModal,
    handleAddTurbine,
    handleDeleteTurbine,
    handleEditTurbine,
    confirmDeleteTurbine,
    confirmEditTurbine,
  }
}
